import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  HostListener,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { Subscription, fromEvent, filter } from 'rxjs';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { OverlayscrollbarsModule } from 'overlayscrollbars-ngx';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';

import { Menu, NavService } from '../../services/navservice';
import { MenuItem, ModulesService } from '../../services/module.service';
import { SecureStorageService } from '../../../core/services/secure-storage.service';
import { AuthService } from '../../../features/auth/services/auth.service';
import { CustomDialogService } from '../../../core/services/custom-dialog.service';
import { AccountDialogConfigs } from '../../configuration/account-dialog.config';
import { CompanyLogoService } from '../../../core/services/company-logo.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, OverlayscrollbarsModule, NgbDropdownModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('submenuAnimation', [
      state(
        'closed',
        style({
          height: '0',
          opacity: '0',
          overflow: 'hidden',
        })
      ),
      state(
        'open',
        style({
          height: '*',
          opacity: '1',
        })
      ),
      transition('closed <=> open', [animate('200ms cubic-bezier(0.4, 0, 0.2, 1)')]),
    ]),
  ],
})
export class SidebarComponent implements OnInit, OnDestroy {
  private readonly navService = inject(NavService);
  private readonly modulesService = inject(ModulesService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly storageService = inject(SecureStorageService);
  private readonly authService = inject(AuthService);
  private readonly dialogService = inject(CustomDialogService);
  private readonly companyLogoService = inject(CompanyLogoService);

  // Signals
  isCollapsed = signal<boolean>(false);

  // State
  scrolled = false;
  menuItems: Menu[] = [];
  isLoadingModules = false;
  modulesError: string | null = null;
  appVersion = '1.0.0';
  userData: any = {};
  companyLogo: string | null = null;

  // Subscriptions
  private readonly subscriptions = new Subscription();

  ngOnInit(): void {
    this.initializeSubscriptions();
    this.loadModules();
    this.setupRouterEvents();
    this.checkInitialCollapsedState();
    this.loadUserData();
    this.subscribeToCompanyLogo();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private initializeSubscriptions(): void {
    // Subscribe to modules loading state
    this.subscriptions.add(
      this.modulesService.loading$.subscribe((loading) => {
        this.isLoadingModules = loading;
        this.cdr.markForCheck();
      })
    );

    // Subscribe to modules error state
    this.subscriptions.add(
      this.modulesService.error$.subscribe((error) => {
        this.modulesError = error;
        this.cdr.markForCheck();
      })
    );

    // Subscribe to menu items
    this.subscriptions.add(
      this.modulesService.menuItems$.subscribe((menuItems) => {
        if (menuItems.length > 0) {
          this.menuItems = this.convertMenuItemsToMenuFormat(menuItems);
          this.navService.updateMenuItems(this.menuItems);

          // Set active state based on current URL
          setTimeout(() => {
            this.setActiveMenuItem(this.router.url);
            this.cdr.markForCheck();
          }, 0);
        }
        this.cdr.markForCheck();
      })
    );

    // Subscribe to NavService items as fallback
    this.subscriptions.add(
      this.navService.items.subscribe((items) => {
        if (this.menuItems.length === 0 && items.length > 0) {
          this.menuItems = items;
          setTimeout(() => {
            this.setActiveMenuItem(this.router.url);
            this.cdr.markForCheck();
          }, 0);
        }
        this.cdr.markForCheck();
      })
    );

    // Listen for window resize
    this.subscriptions.add(
      fromEvent(window, 'resize').subscribe(() => {
        this.handleResize();
      })
    );
  }

  private setupRouterEvents(): void {
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event) => {
          this.setActiveMenuItem(event.urlAfterRedirects);

          // Close sidebar on mobile after navigation
          if (window.innerWidth < 992) {
            this.closeMobileSidebar();
          }
        })
    );
  }

  private checkInitialCollapsedState(): void {
    // Check if sidebar should be collapsed based on screen size or stored preference
    const storedState = localStorage.getItem('sidebarCollapsed');
    if (storedState !== null) {
      this.isCollapsed.set(storedState === 'true');
    } else if (window.innerWidth < 1200 && window.innerWidth >= 992) {
      this.isCollapsed.set(true);
    }

    // Update HTML attribute for global styling
    this.updateDataToggled();
  }

  private loadModules(): void {
    // Modules are loaded by APP_INITIALIZER via permission-initializer.ts
    // which calls modulesService.setNavigationModules().
    // The menuItems$ subscription in initializeSubscriptions() handles rendering.
    // No fallback API call needed — if the initializer failed, the sidebar simply shows no modules.
    const cachedModules = this.modulesService.getCurrentModules();
    if (cachedModules.length === 0) {
      console.warn('Sidebar: No modules loaded from APP_INITIALIZER');
    }
  }

  private convertMenuItemsToMenuFormat(menuItems: MenuItem[]): Menu[] {
    return menuItems.map((item) => ({
      title: item.title,
      headTitle: item.headTitle,
      path: item.path,
      icon: item.icon,
      type: item.type,
      badgeValue: item.badgeValue,
      badgeClass: item.badgeClass,
      active: item.active || false,
      selected: item.selected || false,
      disabled: item.disabled || false,
      dirchange: item.dirchange || false,
      children: item.children ? this.convertMenuItemsToMenuFormat(item.children) : undefined,
    }));
  }

  get companyName(): string {
    return this.userData?.organizationName ?? this.userData?.org_name ?? '';
  }

  get companyInitial(): string {
    const name = this.companyName;
    if (!name) return 'D';
    const words = name.trim().split(/\s+/);
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  }

  get brandFontSize(): string {
    const len = this.companyName.length;
    if (len <= 15) return '1rem';
    if (len <= 22) return '0.875rem';
    if (len <= 30) return '0.8125rem';
    return '0.75rem';
  }

  onCompanyLogoError(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
    this.companyLogo = null;
    this.cdr.markForCheck();
  }

  private subscribeToCompanyLogo(): void {
    this.subscriptions.add(
      this.companyLogoService.logo$.subscribe((logo) => {
        this.companyLogo = logo;
        this.cdr.markForCheck();
      })
    );
  }

  refreshModules(): void {
    this.loadModules();
  }

  toggleSidebar(): void {
    if (window.innerWidth <= 992) {
      this.handleMobileToggle();
    } else {
      this.isCollapsed.set(!this.isCollapsed());
      localStorage.setItem('sidebarCollapsed', String(this.isCollapsed()));
      this.updateDataToggled();
    }
  }

  private handleMobileToggle(): void {
    const html = document.documentElement;
    const currentToggled = html.getAttribute('data-toggled');
    const overlay = document.getElementById('responsive-overlay');

    if (currentToggled === 'open') {
      html.setAttribute('data-toggled', 'close');
      overlay?.classList.remove('active');
    } else {
      html.setAttribute('data-toggled', 'open');
      overlay?.classList.add('active');
    }
  }

  private updateDataToggled(): void {
    const html = document.documentElement;
    if (this.isCollapsed()) {
      html.setAttribute('data-toggled', 'icon-overlay-close');
    } else {
      html.removeAttribute('data-toggled');
    }
  }

  private closeMobileSidebar(): void {
    const overlay = document.getElementById('responsive-overlay');
    if (overlay) {
      overlay.classList.remove('active');
    }
    document.documentElement.setAttribute('data-toggled', 'close');
  }

  onMenuItemClick(event: Event, menuItem: Menu): void {
    // Expand sidebar if collapsed
    if (this.isCollapsed()) {
      this.expandSidebar();
    }

    if (menuItem.type === 'link' && menuItem.path) {
      this.setActiveMenuItem(menuItem.path);
    }
  }

  toggleSubmenu(event: Event, menuItem: Menu): void {
    event.preventDefault();
    event.stopPropagation();

    // Expand sidebar if collapsed, then open the submenu
    if (this.isCollapsed()) {
      this.expandSidebar();
      this.closeOtherMenus(this.menuItems, menuItem);
      menuItem.active = true;
      this.setMenuAncestorsActive(menuItem);
      return;
    }

    // Close other open menus at the same level
    this.closeOtherMenus(this.menuItems, menuItem);

    // Toggle current menu
    menuItem.active = !menuItem.active;

    // If opening a submenu, set parent as selected
    if (menuItem.active) {
      this.setMenuAncestorsActive(menuItem);
    }
  }

  private expandSidebar(): void {
    this.isCollapsed.set(false);
    localStorage.setItem('sidebarCollapsed', 'false');
    this.updateDataToggled();
  }

  private setActiveMenuItem(currentPath: string): void {
    this.clearAllActiveStates(this.menuItems);
    this.findAndActivateMenuItem(this.menuItems, currentPath);
  }

  private clearAllActiveStates(items: Menu[]): void {
    items.forEach((item) => {
      item.selected = false;
      if (item.children) {
        this.clearAllActiveStates(item.children);
      }
    });
  }

  private findAndActivateMenuItem(items: Menu[], currentPath: string): boolean {
    const normalizedCurrentPath = currentPath.replace(/\/$/, '');

    for (const item of items) {
      const normalizedItemPath = item.path?.replace(/\/$/, '') ?? '';

      const isMatch =
        normalizedItemPath === normalizedCurrentPath ||
        (normalizedItemPath.length > 0 &&
          normalizedCurrentPath.startsWith(normalizedItemPath + '/'));

      if (isMatch && item.type === 'link') {
        item.selected = true;
        item.active = true;
        return true;
      }

      if (item.children && item.children.length > 0) {
        const foundInChildren = this.findAndActivateMenuItem(item.children, currentPath);
        if (foundInChildren) {
          item.active = true;
          item.selected = true;
          return true;
        }
      }
    }

    return false;
  }

  private closeOtherMenus(items: Menu[], targetItem: Menu): void {
    items.forEach((item) => {
      if (item !== targetItem && !this.isAncestorOf(item, targetItem)) {
        item.active = false;
        if (item.children) {
          this.closeAllSubmenus(item.children);
        }
      }
    });
  }

  private closeAllSubmenus(items: Menu[]): void {
    items.forEach((item) => {
      item.active = false;
      if (item.children) {
        this.closeAllSubmenus(item.children);
      }
    });
  }

  private isAncestorOf(potentialAncestor: Menu, item: Menu): boolean {
    if (!potentialAncestor.children) return false;

    for (const child of potentialAncestor.children) {
      if (child === item || this.isAncestorOf(child, item)) {
        return true;
      }
    }
    return false;
  }

  private setMenuAncestorsActive(targetItem: Menu): void {
    const parent = this.findParent(this.menuItems, targetItem);
    if (parent) {
      parent.active = true;
      parent.selected = true;
      this.setMenuAncestorsActive(parent);
    }
  }

  private findParent(items: Menu[], targetItem: Menu): Menu | null {
    for (const item of items) {
      if (item.children?.includes(targetItem)) {
        return item;
      }
      if (item.children) {
        const found = this.findParent(item.children, targetItem);
        if (found) return found;
      }
    }
    return null;
  }

  private handleResize(): void {
    const width = window.innerWidth;

    if (width < 992) {
      document.documentElement.setAttribute('data-toggled', 'close');
    } else if (width >= 992) {
      document.documentElement.removeAttribute('data-toggled');
      document.getElementById('responsive-overlay')?.classList.remove('active');
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    this.scrolled = window.scrollY > 10;
  }

  private loadUserData(): void {
    this.userData = this.storageService.getUserData();
    this.subscriptions.add(
      this.authService.currentUser$.subscribe((user) => {
        if (user) {
          this.userData = user;
          this.cdr.markForCheck();
        }
      })
    );
  }

  getUserInitials(): string {
    const name = this.userData?.name;
    if (!name) {
      return 'U';
    }
    const nameParts = name.trim().split(/\s+/);
    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }
    return (
      nameParts[0].charAt(0).toUpperCase() + nameParts[nameParts.length - 1].charAt(0).toUpperCase()
    );
  }

  get hasProfileImage(): boolean {
    const image = this.userData?.profileImage;
    if (!image || typeof image !== 'string') {
      return false;
    }
    const trimmedImage = image.trim();
    if (!trimmedImage) {
      return false;
    }
    const emptyGuidPattern = /^0{8}-0{4}-0{4}-0{4}-0{12}$/;
    if (emptyGuidPattern.test(trimmedImage)) {
      return false;
    }
    const isUrl = /^(https?:\/\/|\/|\.\/|\.\.\/)/i.test(trimmedImage);
    const hasImageExtension = /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i.test(trimmedImage);
    const isBase64 = trimmedImage.startsWith('data:image/');
    return isUrl || hasImageExtension || isBase64;
  }

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
  }

  openAccountDialog(): void {
    const userId = this.userData?.id ?? this.userData?.userId ?? 0;
    this.dialogService
      .open({
        ...AccountDialogConfigs.EDIT_ACCOUNT,
        data: { userId },
      })
      .subscribe({
        next: (result) => {
          if (result?.success) {
            this.userData = this.storageService.getUserData();
            this.cdr.markForCheck();
          }
        },
      });
  }

  onLogout(): void {
    // Clear all storage data before logout
    this.clearAllStorageData();

    this.authService.logout().subscribe({
      next: (response) => {
        if (response?.logoutUrl) {
          globalThis.location.href = response.logoutUrl;
        } else {
          this.router.navigate(['/login']);
        }
      },
      error: () => {
        this.router.navigate(['/login']);
      },
    });
  }

  private clearAllStorageData(): void {
    // Clear session storage
    sessionStorage.clear();

    // Clear local storage (including location data)
    localStorage.clear();

    // Clear all cookies
    this.clearAllCookies();
  }

  private clearAllCookies(): void {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const cookieName = cookie.split('=')[0].trim();
      // Only clear app-specific cookies (with app_ prefix) to avoid affecting third-party cookies
      if (cookieName?.startsWith('app_')) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
      }
    }
  }
}
